import { Fragment, useState } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import {
  AnalystColumn,
  AnalystGridConfig,
  AnalystGridDetail,
  AnalystGridRow,
  asString,
  formatValue,
} from '@circuitry-ai/doc-ui';

function cellText(column: AnalystColumn, value: unknown): string {
  switch (column.type) {
    case 'number':
      return formatValue(Number(value), 'number');
    case 'percent':
      return formatValue(Number(value), 'percent');
    case 'currency':
      return formatValue(Number(value), 'currency');
    case 'days':
      return formatValue(Number(value), 'days');
    default:
      return asString(value);
  }
}

function alignClass(column: AnalystColumn): string {
  if (['number', 'percent', 'currency', 'days'].includes(column.type ?? '')) return 'text-right';
  return 'text-left';
}

function statusPillTone(value: string): string {
  const lower = value.toLowerCase();
  const isPositive = lower.includes('active') || lower.includes('healthy') || lower.includes('good') || lower.includes('on track');
  if (isPositive) return 'bg-success/10 text-success';
  const isNegative = lower.includes('risk') || lower.includes('fail') || lower.includes('error') || lower.includes('overdue') || lower.includes('critical');
  if (isNegative) return 'bg-danger/10 text-danger';
  return 'bg-default-100 text-default-600';
}

function StatusPill({ value }: { readonly value: string }) {
  const tone = statusPillTone(value);
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{value}</span>;
}

function Cell({ column, value }: { readonly column: AnalystColumn; readonly value: unknown }) {
  if (column.type === 'status') return <StatusPill value={asString(value)} />;
  return <span>{cellText(column, value)}</span>;
}

function recordKey(record: Record<string, unknown>): string {
  return Object.values(record).map((value) => asString(value)).join('|');
}

/** Nested drill-through sub-table rendered when a row carries `detail`. */
function NestedDetail({ detail }: { readonly detail: AnalystGridDetail }) {
  return (
    <div className="rounded-md border border-default-200 bg-default-50 p-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-default-200 text-default-500">
            {detail.columns.map((column) => (
              <th key={column.field} className={`px-2 py-1 font-semibold ${alignClass(column)}`}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.rows.map((record) => (
            <tr key={recordKey(record)} className="border-b border-default-100 last:border-0">
              {detail.columns.map((column) => (
                <td key={column.field} className={`px-2 py-1 ${alignClass(column)}`}>
                  <Cell column={column} value={record[column.field]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A single grid row plus its (optional) expandable drill-through detail. */
function GridRowItem({
  row,
  columns,
  hasDetail,
  isOpen,
  onToggle,
}: {
  readonly row: AnalystGridRow;
  readonly columns: AnalystColumn[];
  readonly hasDetail: boolean;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <Fragment>
      <tr
        className={`border-b border-default-100 ${row.detail ? 'cursor-pointer hover:bg-default-50' : ''}`}
        onClick={row.detail ? onToggle : undefined}
      >
        {hasDetail ? (
          <td className="px-2 py-2 text-default-400">
            {row.detail ? <Icon icon={isOpen ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={16} /> : null}
          </td>
        ) : null}
        {columns.map((column) => (
          <td key={column.field} className={`px-3 py-2 ${alignClass(column)}`} style={{ fontFamily: 'Poppins, sans-serif', fontSize: 12 }}>
            <Cell column={column} value={row[column.field]} />
          </td>
        ))}
      </tr>
      {isOpen && row.detail ? (
        <tr>
          <td colSpan={columns.length + (hasDetail ? 1 : 0)} className="bg-default-50 px-3 py-2">
            <NestedDetail detail={row.detail} />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

/** Prev/Next pagination footer, shown when `pageSize` splits the rows. */
function GridPagination({
  currentPage,
  totalPages,
  pageSize,
  total,
  onPageChange,
}: {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly total: number;
  readonly onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border-t border-default-200 px-3 py-2 text-xs text-default-500">
      <span>
        {t('{{from}}–{{to}} of {{total}}', {
          from: total === 0 ? 0 : (currentPage - 1) * pageSize + 1,
          to: Math.min(currentPage * pageSize, total),
          total,
        })}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded p-1 hover:bg-default-100 disabled:opacity-40"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          aria-label={t('Previous page')}
        >
          <Icon icon="mdi:chevron-left" width={16} />
        </button>
        <button
          type="button"
          className="rounded p-1 hover:bg-default-100 disabled:opacity-40"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          aria-label={t('Next page')}
        >
          <Icon icon="mdi:chevron-right" width={16} />
        </button>
      </div>
    </div>
  );
}

interface AnalystGridProps {
  readonly config: AnalystGridConfig;
}

/** Renders a simple table or a drill-through grid (contract §7). */
export function AnalystGrid({ config }: AnalystGridProps) {
  const columns = config.columns ?? [];
  const rows = config.rows ?? [];
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const hasDetail = rows.some((row) => row.detail);
  const paginated = typeof config.pageSize === 'number' && config.pageSize > 0;
  const pageSize = config.pageSize && config.pageSize > 0 ? config.pageSize : Math.max(rows.length, 1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="rounded-md border border-default-200 bg-content1">
      {config.title ? (
        <div className="border-b border-default-200 p-3">
          <span className="text-sm font-semibold text-primary" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {config.title}
          </span>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-default-200 bg-default-50 text-primary">
              {hasDetail ? <th className="w-8 px-2 py-2" /> : null}
              {columns.map((column) => (
                <th
                  key={column.field}
                  className={`px-3 py-2 font-semibold ${alignClass(column)}`}
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 13 }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row: AnalystGridRow) => {
              const key = recordKey(row);
              return (
                <GridRowItem
                  key={key}
                  row={row}
                  columns={columns}
                  hasDetail={hasDetail}
                  isOpen={expanded === key}
                  onToggle={() => setExpanded(expanded === key ? null : key)}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {paginated && totalPages > 1 ? (
        <GridPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          total={rows.length}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

export default AnalystGrid;
